import React, { useState, useEffect } from 'react';
import { Modal, Button, Skeleton, Empty } from 'antd';
import { CalendarOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import ClinicianCard from 'vl-common/src/components/BookAppointment/ClinicianCard';
import { AppointmentSlot } from 'vl-common/src/schemas/getAppointmentSlots';
import { useCliniciansForSlot } from '../../../hooks/useCliniciansForSlot';
import { BookingData } from '../../../lib/context/BookingContext/bookingTypes';

const StyledModal = styled(Modal)`
  .ant-modal-content {
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ant-modal-body {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 24px;
    flex: 1;
  }
`;

const ModalHeaderWrapper = styled.div`
  margin-bottom: 20px;
  flex-shrink: 0;

  .title {
    font-size: 20px;
    font-weight: 700;
    color: var(--productPrimaryDark);
    margin-bottom: 12px;
  }

  .slot-pill {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #f7f9fa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 12px 18px;

    .slot-pill-icon {
      position: static !important;
      float: none !important;
      flex-shrink: 0;
      font-size: 20px;
      color: #8c8c8c;
      margin: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      color: #8c8c8c;
      letter-spacing: 0.5px;
    }

    .value {
      font-size: 15px;
      font-weight: 700;
      color: var(--productPrimaryDark);
    }
  }
`;

const CliniciansListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
`;

const ClinicianRowItem = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #ffffff;
  flex-shrink: 0;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: stretch;
  }

  .clinician-card-wrapper {
    flex: 1;
    min-width: 0;
  }

  .select-action-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 24px;
    border-top: 1px solid #e8e8e8;
    background: #fafafa;
    align-self: stretch;

    @media (min-width: 640px) {
      border-top: none;
      border-left: 1px solid #e8e8e8;
      min-width: 140px;
    }
  }
`;

const SelectButton = styled(Button)`
  && {
    background-color: var(--productPrimary);
    color: var(--productPrimaryDark);
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    font-weight: 700;
    border-radius: 6px;
    padding: 0 24px;
    height: 40px;

    &:hover,
    &:focus,
    &:focus-visible,
    &:active {
      background-color: var(--productPrimary) !important;
      color: var(--productPrimaryDark) !important;
      opacity: 0.9;
    }

    span {
      color: var(--productPrimaryDark) !important;
      font-weight: 700;
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
  background: #ffffff;
  flex-shrink: 0;

  .page-indicator {
    font-size: 14px;
    font-weight: 600;
    color: var(--productPrimaryDark);
  }

  .nav-button {
    font-weight: 600;
    border-radius: 6px;
  }
`;

type SelectClinicianModalProps = {
  open: boolean;
  onCancel: () => void;
  selectedSlot: AppointmentSlot | null;
  patientGuid?: string;
  clinSubtype?: string;
  clinSpeccat?: string;
  clinSpecarea?: string;
  apptTypeCode?: string;
  caseId?: number | null;
  pageSize?: number;
  onSelectClinicianSlot: (data: Pick<BookingData, 'apptDate' | 'clinician'>) => void;
};

const getEndTimeStr = (selectedSlot: SelectClinicianModalProps['selectedSlot']) => {
  if (selectedSlot?.slot_end_datetime) {
    return dayjs(selectedSlot.slot_end_datetime).add(1, 'minute').format('HH:mm');
  }
  if (selectedSlot?.slot_lengh_mins) {
    return dayjs(selectedSlot.slot_datetime)
      .add(selectedSlot.slot_lengh_mins + 1, 'minute')
      .format('HH:mm');
  }

  return '';
};

export const SelectClinicianModal: React.FC<SelectClinicianModalProps> = ({
  open,
  onCancel,
  selectedSlot,
  patientGuid,
  clinSubtype,
  clinSpeccat,
  clinSpecarea,
  apptTypeCode,
  caseId,
  pageSize = 5,
  onSelectClinicianSlot
}) => {
  const { t } = useTranslation('common');
  const slotDatetime = selectedSlot?.slot_datetime;
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
    }
  }, [open, slotDatetime]);

  const {
    data: clinicians = [],
    isLoading,
    isError
  } = useCliniciansForSlot({
    datetime: slotDatetime,
    patientGuid,
    caseId,
    clinSubtype,
    clinSpeccat,
    clinSpecarea,
    apptTypeCode,
    rank: '1',
    page: currentPage,
    rows: pageSize,
    enabled: open && !!slotDatetime
  });

  const hasPrevPage = currentPage > 1;
  const hasNextPage = clinicians.length === pageSize;
  const showPagination = hasPrevPage || hasNextPage;

  const formattedDate = slotDatetime ? dayjs(slotDatetime).format('ddd DD MMM') : '';
  const startTimeStr = slotDatetime ? dayjs(slotDatetime).format('HH:mm') : '';

  const endTimeStr = getEndTimeStr(selectedSlot);

  const firstFastestClinician = clinicians.find((c: any) => c?.fastest_flag)?.clin_user_guid;

  const renderContent = () => {
    if (isLoading) {
      return <Skeleton active paragraph={{ rows: 6 }} />;
    }

    if (isError || !clinicians.length) {
      return <Empty description={t('No available clinicians found for this slot')} />;
    }

    return (
      <>
        <CliniciansListContainer>
          {clinicians.map((clinician: any, index: number) => {
            const clinicianGuid = clinician.clin_user_guid || clinician.user_guid;
            const uniqueKey = clinicianGuid
              ? `${clinicianGuid}-${slotDatetime || index}`
              : `${slotDatetime || 'clinician'}-${index}`;

            const isFirstFastest = Boolean(
              clinician.fastest_flag || (firstFastestClinician && firstFastestClinician === clinicianGuid)
            );

            return (
              <ClinicianRowItem key={uniqueKey}>
                <div className="clinician-card-wrapper">
                  <ClinicianCard
                    clinician={clinician}
                    isFirstFastest={isFirstFastest}
                    tags={clinician.info_tags || []}
                    timezone={clinician.timezone}
                  />
                </div>
                <div className="select-action-container">
                  <SelectButton
                    data-testid="select-clinician-button"
                    onClick={() => {
                      onSelectClinicianSlot({
                        apptDate: slotDatetime ? dayjs(slotDatetime).toDate() : undefined,
                        clinician
                      });
                      onCancel();
                    }}
                  >
                    {t('Select')}
                  </SelectButton>
                </div>
              </ClinicianRowItem>
            );
          })}
        </CliniciansListContainer>

        {showPagination && (
          <PaginationWrapper>
            <Button
              data-testid="previous-clinician-page-button"
              className="nav-button"
              icon={<LeftOutlined />}
              disabled={!hasPrevPage}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              {t('Previous')}
            </Button>

            <span className="page-indicator">{t('Page {{current}}', { current: currentPage })}</span>

            <Button
              data-testid="next-clinician-page-button"
              className="nav-button"
              disabled={!hasNextPage}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              {t('Next')} <RightOutlined />
            </Button>
          </PaginationWrapper>
        )}
      </>
    );
  };

  return (
    <StyledModal open={open} onCancel={onCancel} footer={null} width={760} centered destroyOnHidden>
      <ModalHeaderWrapper>
        <div className="title">{t('Select a Clinician')}</div>
        <div className="slot-pill">
          <CalendarOutlined className="slot-pill-icon" />
          <div>
            <div className="label">{t('DATE & TIME')}</div>
            <div className="value">
              {formattedDate} {startTimeStr && endTimeStr ? `${startTimeStr}–${endTimeStr}` : startTimeStr}
            </div>
          </div>
        </div>
      </ModalHeaderWrapper>

      {renderContent()}
    </StyledModal>
  );
};
